import { useState, useRef } from "react";

import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { LoadingButton } from "@atlaskit/button";
import { FormFooter } from "@atlaskit/form";
import Heading from "@atlaskit/heading";
import { Box, Flex, Inline, Stack, xcss } from "@atlaskit/primitives";
import {
    SubmitButton,
    SubmitButtonRef
} from "@atlassian/invite-people/dist/types/types";

import { useSearchParameters } from "@/components/signup/hooks";
import { ufoEventOnboardingInviteUsers } from "@/components/signup/utils";

import { ANALYTICS_PREFIX } from "../../../../common/constants";
import { PageInviteLeftProps } from "../../../../common/types";
import { useWizardConfiguration } from "../../../../controllers";
import { onInviteUsersRedirectFailureInvite } from "../../../../utils/redirect-logging";
import { getEmailListAnalytics } from "../utils";

import { InvitePeopleWrapper } from "./invite-people-wrapper";
import messages from "./messages";
import { ShareLink } from "./share-link";

const FAILSAFE_REDIRECT_TIMEOUT = 20000;

export const LeftHandSidePageInvite = ({
    onClickNext
}: PageInviteLeftProps) => {
    const [isInvitingUsers, setIsInvitingUsers] = useState(false);
    const [isSubmittingInvite, setIsSubmittingInvite] = useState(false);
    const [isSubmittingSkip, setIsSubmittingSkip] = useState(false);

    const [emailList, setEmailList] = useState<string[]>([]);
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const {
        teamType,
        projectDetails: { template }
    } = useWizardConfiguration();
    const { signupId } = useSearchParameters();

    const submitRef = useRef<HTMLButtonElement & SubmitButton>(null);

    const onInviteStart = () => {
        setIsInvitingUsers(true);
    };

    const onClickNextSubmit = async () => {
        setIsSubmittingInvite(true);
        ufoEventOnboardingInviteUsers.setCustomData({ signupId });

        const { emailCount, isCrossDomain } = getEmailListAnalytics(emailList);

        await Promise.resolve(
            createAnalyticsEvent({
                action: "clicked",
                actionSubject: "button",
                actionSubjectId: `${ANALYTICS_PREFIX}InviteSubmitButton`,
                eventType: "ui",
                attributes: {
                    emailCount,
                    isCrossDomain,
                    teamType,
                    template
                }
            }).fire()
        );

        if (emailList.length === 0) {
            onClickNext();
            return;
        }

        setTimeout(() => {
            onInviteUsersRedirectFailureInvite("time-out");
            onClickNext();
        }, FAILSAFE_REDIRECT_TIMEOUT);

        //@ts-ignore
        await submitRef?.current?.onClick();
    };

    const onClickNextDoThisLater = () => {
        setIsSubmittingSkip(true);

        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: `${ANALYTICS_PREFIX}InviteSkipButton`,
            eventType: "ui"
        }).fire();

        onClickNext();
    };

    return (
        <>
            <Flex
                direction="column"
                justifyContent="space-between"
                xcss={containerStyles}
            >
                <Stack space="space.400">
                    <Stack space="space.150">
                        <Heading as="h1" size="xlarge">
                            <FormattedMessage
                                {...(isInvitingUsers
                                    ? messages.titleSubmitting
                                    : messages.title)}
                            />
                        </Heading>
                        <Box xcss={subtextStyles}>
                            <FormattedMessage
                                {...(isInvitingUsers
                                    ? messages.bodyLineOneSubmitting
                                    : messages.bodyLineOne)}
                            />
                        </Box>
                    </Stack>
                    <Flex direction="column" alignItems="start">
                        <ShareLink />
                        <InvitePeopleWrapper
                            submitRef={submitRef as SubmitButtonRef}
                            onClickNext={onClickNext}
                            onInviteStart={onInviteStart}
                            setEmailList={setEmailList}
                        />
                    </Flex>
                </Stack>
            </Flex>
            <FormFooter>
                <Flex
                    justifyContent="end"
                    alignItems="center"
                    xcss={footerStyles}
                >
                    <Inline
                        alignBlock="center"
                        alignInline="end"
                        space="space.050"
                    >
                        <LoadingButton
                            appearance="subtle"
                            onClick={() => {
                                onClickNextDoThisLater();
                            }}
                            isDisabled={
                                emailList.length > 0 ||
                                isSubmittingInvite ||
                                isInvitingUsers
                            }
                            isLoading={isSubmittingSkip}
                        >
                            <FormattedMessage {...messages.doThisLater} />
                        </LoadingButton>
                        <LoadingButton
                            appearance="primary"
                            onClick={onClickNextSubmit}
                            isDisabled={isSubmittingSkip}
                            isLoading={isInvitingUsers || isSubmittingInvite}
                            ref={submitRef}
                        >
                            <FormattedMessage
                                {...(emailList.length > 0
                                    ? messages.inviteAndGoToJira
                                    : messages.goToJira)}
                            />
                        </LoadingButton>
                    </Inline>
                </Flex>
            </FormFooter>
        </>
    );
};

const containerStyles = xcss({
    height: "100%"
});

const subtextStyles = xcss({
    color: "color.text.subtle",
    minHeight: "size.500"
});

const footerStyles = xcss({
    width: "100%",
    color: "color.text.subtlest"
});
